import React, { useState, useEffect, useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import { Line } from 'react-chartjs-2';

// --- KEYFRAMES ---

export const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

// --- STYLED COMPONENTS ---

export const Spinner = styled.div`
  width: 18px;
  height: 18px;
  border: 2px solid rgba(0, 0, 0, 0.1);
  border-top: 2px solid #0e141b;
  border-radius: 50%;
  animation: ${spin} 0.8s linear infinite;
  display: inline-block;
  margin-left: 8px;
  vertical-align: middle;
`;

export const StatWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 15px;
`;

export const BackBtn = styled.button`
  background: #e2e8f0;
  border: none;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 600;
  margin-top: 10px;
`;

export const GemToggleBtn = styled.button`
  background: #0e141b;
  color: #ffffff;
  border: 1px solid #cbd5e1;
  padding: 6px 14px;
  border-radius: 20px;
  cursor: pointer;
  font-weight: 700;
  text-transform: capitalize;
  font-size: 0.85rem;
  transition: all 0.2s ease;
  &:hover {
    opacity: 0.9;
  }
`;

export const FeatureContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 8px;
  margin: 12px 0;
  padding: 12px;
  background: #f1f5f9;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

export const FeatureItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  background: white;
  padding: 6px 8px;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

export const FeatureLabel = styled.span`
  font-size: 0.7rem;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
`;

export const FeatureValue = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: #0f172a;
`;

export const DisplayDiv = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  margin: 12px 0;
`;

export const ChartWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  height: 50vh;
  box-sizing: border-box;
  overflow: hidden;
`;

// --- HELPER FUNCTIONS ---

export function shortenValue(val) {
  if (typeof val === 'string' && isNaN(val)) return val;
  return Number(val).toFixed(2);
}

// --- PLAYER PAGE COMPONENT ---

const PlayerPage = ({ player, onBack, view = 'points', team = '' }) => {
  const GEMS = ['amethyst3', 'emerald3', 'sapphire3', 'amethyst1', 'emerald1', 'sapphire1'];

  const [gem, setGem] = useState('amethyst3');
  const [windowSize, setWindowSize] = useState(10);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  const [allPredData, setAllPredData] = useState({});
  const [allTodayPreds, setAllTodayPreds] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [allFeatures, setAllFeatures] = useState({});

  const GEM_COLORS = {
    rock: { border: 'rgb(107, 114, 128)', bg: 'rgba(107, 114, 128, 0.5)' },
    ruby: { border: 'rgb(225, 29, 72)', bg: 'rgba(225, 29, 72, 0.5)' },
    diamond: { border: 'rgb(56, 189, 248)', bg: 'rgba(56, 189, 248, 0.5)' },
    quartz: { border: 'rgb(236, 72, 153)', bg: 'rgba(236, 72, 153, 0.5)' },
    beryl: { border: 'rgb(16, 185, 129)', bg: 'rgba(16, 185, 129, 0.5)' },
    mica: { border: 'rgb(245, 158, 11)', bg: 'rgba(245, 158, 11, 0.5)' },
  };

  const fetchGemTodayPred = async (selectedPlayer, targetView, activeGem) => {
    const res = await axios({
      method: 'post',
      url: `https://82jsvqje43.execute-api.us-east-2.amazonaws.com/wnba_dev/nba`,
      withCredentials: false,
      data: {
        player: selectedPlayer,
        path: targetView,
        gem: activeGem,
      },
    });
    const numbers_res = res.data.match(/[\d.]+/g);
    return numbers_res ? Number(numbers_res[0]) : null;
  };

  const fetchGemFeatures = async (targetView, activeGem) => {
    const res = await axios({
      method: 'post',
      url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
      withCredentials: false,
      data: {
        option: 'features',
        target: targetView,
        gem: activeGem,
      },
    });
    return res.data;
  };

  const fetchGemChartData = async (selectedPlayer, activeGem, teamStr) => {
    try {
      if (!teamStr || !teamStr.includes('_')) return null;
      const parts = teamStr.split('_');
      if (parts.length < 3) return null;

      const month = parts[1];
      const day = parts[2].slice(0, 2);

      const res = await axios({
        method: 'post',
        url: `https://sx7vpiy3ij.execute-api.us-east-2.amazonaws.com/admin_dev/team-pull`,
        withCredentials: false,
        data: {
          option: 'player-pred',
          player: selectedPlayer,
          day,
          month,
          gem: activeGem,
        },
      });

      return res.data?.data || null;
    } catch (err) {
      console.error(`Chart data error [${activeGem}]:`, err);
      return null;
    }
  };

  useEffect(() => {
    let isSubscribed = true;

    const fetchAllGems = async () => {
      setIsLoading(true);

      const gemPromises = GEMS.map(async (g) => {
        const [todayRes, featRes, chartRes] = await Promise.allSettled([
          fetchGemTodayPred(player, view, g),
          fetchGemFeatures(view, g),
          fetchGemChartData(player, g, team),
        ]);

        if (!isSubscribed) return;

        if (todayRes.status === 'fulfilled') {
          setAllTodayPreds((prev) => ({ ...prev, [g]: todayRes.value }));
        }
        if (featRes.status === 'fulfilled') {
          const data = featRes.value?.data || featRes.value || [];
          const formatted = Array.isArray(data) ? data : Object.entries(data);
          setAllFeatures((prev) => ({ ...prev, [g]: formatted }));
        }
        if (chartRes.status === 'fulfilled') {
          setAllPredData((prev) => ({ ...prev, [g]: chartRes.value }));
        }
      });

      await Promise.allSettled(gemPromises);

      if (isSubscribed) {
        setIsLoading(false);
      }
    };

    fetchAllGems();

    return () => {
      isSubscribed = false;
    };
  }, [player, view, team]);

  const cycleGem = () => {
    const currentIndex = GEMS.indexOf(gem);
    const nextGem = GEMS[(currentIndex + 1) % GEMS.length];
    setGem(nextGem);
  };

  const playerChartData = useMemo(() => {
    const activeData = allPredData[gem];
    const rawList = activeData && activeData[view] ? activeData[view] : [];

    if (!Array.isArray(rawList) || rawList.length === 0) return [];

    const parseValue = (val) => {
      if (val === null || val === undefined) return null;
      if (Array.isArray(val)) return Number(val[0]);
      if (typeof val === 'string') {
        const cleaned = val.replace(/[\[\]\s]/g, '');
        return cleaned === '' ? null : Number(cleaned);
      }
      return Number(val);
    };

    return rawList
      .filter((row) => row[0] !== '0')
      .map((row, i) => ({
        date: row[0],
        pred: parseValue(row[1]),
        actual: parseValue(row[2]),
        label: row[0],
        _sortKey: i,
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date) || a._sortKey - b._sortKey);
  }, [allPredData, gem, view]);

  useEffect(() => {
    if (playerChartData.length === 0) return;
    const end = playerChartData.length - 1;
    const start = Math.max(0, end - windowSize + 1);
    setStartIndex(start);
    setEndIndex(end);
  }, [playerChartData.length, windowSize]);

  const handleBack = () => {
    setStartIndex((prev) => Math.max(0, prev - 1));
    setEndIndex((prev) => Math.max(windowSize - 1, prev - 1));
  };

  const handleForward = () => {
    setStartIndex((prev) => Math.min(playerChartData.length - windowSize, prev + 1));
    setEndIndex((prev) => Math.min(playerChartData.length - 1, prev + 1));
  };

  const isBackDisabled = startIndex === 0;
  const isForwardDisabled = endIndex >= playerChartData.length - 1;

  const cycleWindowSize = () => {
    setWindowSize((prev) => (prev >= 20 ? 5 : prev + 5));
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { left: 0, right: 0, top: 0, bottom: 0 } },
    scales: { x: { ticks: { autoSkip: false } } },
  };

  const chartDataForPlayer = useMemo(() => {
    if (playerChartData.length === 0) return { labels: [], datasets: [] };

    const visible = playerChartData.slice(startIndex, endIndex + 1);
    const currentColor = GEM_COLORS[gem] || GEM_COLORS.rock;

    return {
      labels: visible.map((d) => d.date),
      datasets: [
        {
          label: `Predicted (${gem.toUpperCase()})`,
          data: visible.map((d) => d.pred),
          borderColor: currentColor.border,
          backgroundColor: currentColor.bg,
          fill: false,
          tension: 0.2,
        },
        {
          label: 'Actual',
          data: visible.map((d) => d.actual),
          borderColor: 'rgb(34, 197, 94)',
          backgroundColor: 'rgba(34, 197, 94, 0.5)',
          fill: false,
          tension: 0.2,
        },
      ],
    };
  }, [playerChartData, startIndex, endIndex, gem]);

  const currentTodayPred = allTodayPreds[gem];
  const currentFeatures = allFeatures[gem] || [];
  const EXCLUDED_METRICS = ['y_train_range', 'y_train_std', 'y_test_range', 'y_true'];

  return (
    <div style={{ padding: '1rem' }}>
      <DisplayDiv>
        <h2 style={{ marginTop: '10px' }}>{player}</h2>
        <BackBtn onClick={onBack}>← Back</BackBtn>
        <StatWrapper>
          <h3 style={{ margin: 0 }}>Today's Pred:</h3>
          {isLoading ? <Spinner /> : <h4 style={{ margin: 0 }}>{currentTodayPred}</h4>}
        </StatWrapper>

        <GemToggleBtn onClick={cycleGem} disabled={isLoading}>
          {isLoading ? 'Loading all gems...' : gem}
        </GemToggleBtn>

        <button onClick={handleBack} disabled={isBackDisabled || isLoading}>
          ← Back
        </button>
        <button onClick={handleForward} disabled={isForwardDisabled || isLoading}>
          Forward →
        </button>
        <button onClick={cycleWindowSize} disabled={isLoading}>
          Show last {windowSize}
        </button>
      </DisplayDiv>

      {!isLoading && currentFeatures.length > 0 && (
        <FeatureContainer>
          {currentFeatures
            .filter(([metricKey]) => !EXCLUDED_METRICS.includes(metricKey))
            .map(([metricKey, val]) => (
              <FeatureItem key={metricKey}>
                <FeatureLabel>{metricKey}</FeatureLabel>
                <FeatureValue>{shortenValue(val)}</FeatureValue>
              </FeatureItem>
            ))}
        </FeatureContainer>
      )}

      <ChartWrapper style={{ opacity: isLoading ? 0.4 : 1, transition: 'opacity 0.2s ease' }}>
        <Line data={chartDataForPlayer} options={options} width="80vw" height="50vh" />
      </ChartWrapper>
    </div>
  );
};

export default PlayerPage;